import {
  ColumnDataTypes,
  FilterOperators,
  FilterOptionTypes,
  type ColumnConfig,
  type FilterOperatorObject,
} from '@/components/DataTable';
import {
  DEVICE_FILTERABLE_FIELDS,
  deviceOsFamilySchema,
  type DeviceListItem,
} from '@kirin/api-client/contracts/device-inventory';
import { endpointFilterConfig } from '@/components/shared/shadow-ai';
import type { TimestampFormat } from '@/contexts/TimestampFormatContext';
import { queryKeys } from '@/hooks/api/queryKeys';
import {
  AiToolsCell,
  DeviceAgentVersionCell,
  DeviceAlertsCell,
  EndpointNameCell,
  LastSeenCell,
  OsCell,
  PeopleSeenCell,
} from './CellRenderers';
import { fetchDeviceFilterOptions } from './api';
import {
  DEVICE_FILTER_URL_KEYS,
  OS_FAMILY_LABELS,
  SHARED_FILTER_VALUES,
} from './constants';

const EnumOperators: FilterOperatorObject[] = [
  { value: FilterOperators.INCLUDES, label: 'Includes' },
  { value: FilterOperators.EXCLUDES, label: 'Excludes' },
];

const EnumOperatorsWithEmpty: FilterOperatorObject[] = [
  ...EnumOperators,
  { value: FilterOperators.IS_EMPTY, label: 'Is Empty' },
  { value: FilterOperators.IS_NOT_EMPTY, label: 'Is Not Empty' },
];

export interface DeviceColumnOptions {
  /** `agent_auto_update` on: a state other than up to date shows in the version cell. */
  showUpdateState: boolean;
  /** The list response's latest agent version; null when unknown. */
  latestAgentVersion: string | null;
  /** `endpoint_identifier` on: the Alerts (7d) count links to the alerts. */
  linkToAlerts: boolean;
}

/**
 * KIR-5412 — Inventory → Endpoints columns (Mockup 1): Endpoint name, OS,
 * Agent version, People seen, AI tools, # MCPs, Alerts (7d), Last seen.
 * Filters: endpoint name or ID (KIR-5098), OS, agent version, shared.
 */
export function createDeviceColumns(
  organizationId: string,
  timestampFormat: TimestampFormat,
  { showUpdateState, latestAgentVersion, linkToAlerts }: DeviceColumnOptions
): ColumnConfig<DeviceListItem>[] {
  return [
    {
      id: DEVICE_FILTERABLE_FIELDS.ENDPOINT,
      header: 'Endpoint name',
      accessor: 'name',
      minWidth: 160,
      maxWidth: 260,
      renderCell: ({ row }) => (
        <EndpointNameCell row={row} organizationId={organizationId} />
      ),
      sortable: true,
      sortField: 'name',
      sortKey: 'n',
      ...endpointFilterConfig({
        getQueryKey: params =>
          queryKeys.devices.filterOptions(organizationId, 'name', {
            search: params.search,
            limit: params.limit,
          }),
        queryFn: params =>
          fetchDeviceFilterOptions(organizationId, 'name', params),
      }),
      statePersistence: {
        urlKey: DEVICE_FILTER_URL_KEYS[DEVICE_FILTERABLE_FIELDS.ENDPOINT],
      },
    },
    {
      id: DEVICE_FILTERABLE_FIELDS.OS,
      header: 'OS',
      accessor: 'osFamily',
      minWidth: 110,
      maxWidth: 200,
      renderCell: ({ row }) => <OsCell row={row} />,
      sortable: true,
      sortField: 'os',
      sortKey: 'os',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperators,
      statePersistence: {
        urlKey: DEVICE_FILTER_URL_KEYS[DEVICE_FILTERABLE_FIELDS.OS],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: deviceOsFamilySchema.options.map(value => ({
          value,
          label: OS_FAMILY_LABELS[value],
        })),
      },
    },
    {
      id: DEVICE_FILTERABLE_FIELDS.AGENT_VERSION,
      header: 'Agent version',
      accessor: 'agentVersion',
      minWidth: 130,
      maxWidth: 260,
      renderCell: ({ row }) => (
        <DeviceAgentVersionCell
          row={row}
          latestAgentVersion={latestAgentVersion}
          showUpdateState={showUpdateState}
        />
      ),
      sortable: true,
      sortField: 'agentVersion',
      sortKey: 'av',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperatorsWithEmpty,
      statePersistence: {
        urlKey: DEVICE_FILTER_URL_KEYS[DEVICE_FILTERABLE_FIELDS.AGENT_VERSION],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        pagination: { mode: 'offset', limit: 20 },
        getQueryKey: params =>
          queryKeys.devices.filterOptions(organizationId, 'agentVersion', {
            search: params.search,
            limit: params.limit,
          }),
        queryFn: params =>
          fetchDeviceFilterOptions(organizationId, 'agentVersion', params),
        queryConfig: { staleTime: 2 * 60 * 1000 },
      },
    },
    {
      id: DEVICE_FILTERABLE_FIELDS.SHARED,
      header: 'People seen',
      accessor: 'peopleSeenCount',
      minWidth: 170,
      maxWidth: 280,
      renderCell: ({ row }) => <PeopleSeenCell row={row} />,
      sortable: true,
      sortField: 'peopleSeenCount',
      sortKey: 'ps',
      filterable: true,
      dataType: ColumnDataTypes.BOOLEAN,
      filterOperators: EnumOperators,
      statePersistence: {
        urlKey: DEVICE_FILTER_URL_KEYS[DEVICE_FILTERABLE_FIELDS.SHARED],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [
          { value: SHARED_FILTER_VALUES.shared, label: 'Shared' },
          { value: SHARED_FILTER_VALUES.single, label: 'Single user' },
        ],
      },
    },
    {
      id: 'aiToolCount',
      header: 'AI tools',
      accessor: 'aiToolCount',
      minWidth: 90,
      maxWidth: 120,
      renderCell: ({ row }) => <AiToolsCell row={row} />,
      sortable: true,
      sortField: 'aiToolCount',
      sortKey: 'ai',
    },
    {
      id: 'mcpServerCount',
      header: '# MCPs',
      accessor: 'mcpServerCount',
      minWidth: 90,
      maxWidth: 120,
      renderCell: ({ row }) => <span>{row.mcpServerCount}</span>,
      sortable: true,
      sortField: 'mcpServerCount',
      sortKey: 'mc',
    },
    {
      id: 'alertCount',
      header: 'Alerts (7d)',
      accessor: 'alertCount',
      minWidth: 150,
      maxWidth: 200,
      renderCell: ({ row }) => (
        <DeviceAlertsCell
          count={row.alertCount}
          maxSeverity={row.maxAlertSeverity}
          machineIds={row.machineIds}
          organizationId={organizationId}
          linkToAlerts={linkToAlerts}
        />
      ),
      sortable: true,
      sortField: 'alertCount',
      sortKey: 'ac',
    },
    {
      id: 'lastSeenAt',
      header: 'Last seen',
      accessor: 'lastSeenAt',
      timestamp: true,
      renderCell: ({ row }) => (
        <LastSeenCell value={row.lastSeenAt} format={timestampFormat} />
      ),
      sortable: true,
      sortField: 'lastSeenAt',
      sortKey: 'ls',
    },
  ];
}
